import React, { useState, useEffect } from 'react';
import { X, MoreHorizontal, Copy, Search, Download } from 'lucide-react';
import Pagination from './Pagination';
import noImage from '../assets/img/brand/noImage.png';
import apiHelper from '../apiHelper';
import DiscardConfirmationModal from './DiscardConfirmationModal';

const ITEMS_PAGE_SIZE = 10;

// ─── Status config (matches PromotionalCampaign) ──────────────────────────────
const STATUS_CONFIG = {
    Upcoming:            { bg: '#F8F9FA', color: '#6C757D', border: '1px solid #6C757D', dotColor: '#6C757D' },
    Ongoing:             { bg: '#D1E7DD', color: '#198754', border: '1px solid #198754', dotColor: '#198754' },
    Cancelled:           { bg: '#F8D7DA', color: '#DC3545', border: '1px solid #DC3545', dotColor: '#DC3545' },
    'Ended-Early':       { bg: '#E9ECEF', color: '#212529', border: '1px solid #212529', dotColor: '#6C757D' },
    'Ended-On schedule': { bg: '#E9ECEF', color: '#212529', border: '1px solid #212529', dotColor: '#6C757D' },
};

// ─── Sort icons (local, no shared component change) ───────────────────────────
const SortUp   = ({ active }) => (
    <svg width={10} height={10} viewBox="0 0 24 24" fill={active ? '#212529' : '#6C757D'} xmlns="http://www.w3.org/2000/svg" >
        <path d="M12 8l6 6H6l6-6z" />
    </svg>
);
const SortDown = ({ active }) => (
    <svg width={10} height={10} viewBox="0 0 24 24" fill={active ? '#212529' : '#6C757D'} xmlns="http://www.w3.org/2000/svg" style={{marginTop:'-7px'}}>
        <path d="M12 16l-6-6h12l-6 6z" />
    </svg>
);
const SortableCol = ({ label, field, sortField, sortOrder, onSort, style = {} }) => (
    <th style={{ padding: '10px', textAlign: 'left', background: '#E9ECEF', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529', cursor: 'pointer', whiteSpace: 'nowrap', ...style }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {label}
            <span onClick={() => onSort(field)} style={{ display: 'flex', flexDirection: 'column', lineHeight: 0 }}>
                <SortUp   active={sortField === field && sortOrder === 'asc'}  />
                <SortDown active={sortField === field && sortOrder === 'desc'} />
            </span>
        </span>
    </th>
);
const ColorCircle= ({color}) =>(<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <circle cx="6" cy="6" r="5.5" stroke="black"/>
  <circle cx="6.00007" cy="5.99995" r="4.28571" fill={color}/>
</svg>)
// ─── Main Drawer ──────────────────────────────────────────────────────────────
const CampaignInfoDrawer = ({ row, onClose, onEdit, onCancelCampaign, onEndCampaign }) => {
    const [activeTab,        setActiveTab]        = useState('general');
    const [copied,           setCopied]           = useState(false);
    const [moreOpen,         setMoreOpen]         = useState(false);
    const [itemSearchInput,  setItemSearchInput]  = useState('');
    const [itemSearch,       setItemSearch]       = useState('');
    const [itemPage,         setItemPage]         = useState(1);
    const [itemSortField,    setItemSortField]    = useState(null);
    const [itemSortOrder,    setItemSortOrder]    = useState('asc');
    const [showCancelModal,  setShowCancelModal]  = useState(false);
    const [showEndModal,     setShowEndModal]     = useState(false);
    const [items,            setItems]            = useState([]);
    const [itemsLoading,     setItemsLoading]     = useState(false);
    const [isDownloading,    setIsDownloading]    = useState(false);

    useEffect(() => {
        if (activeTab !== 'items' || !row?.id) return;
        setItemsLoading(true);
        apiHelper(`sales/promotional-campaigns/${row.id}/items/`, 'GET')
            .then(res => res.ok ? res.json() : [])
            .then(data => setItems(Array.isArray(data) ? data : []))
            .catch(() => setItems([]))
            .finally(() => setItemsLoading(false));
    }, [activeTab, row?.id]);

    const handleDownload = async () => {
        if (isDownloading || !row?.id) return;
        setIsDownloading(true);
        try {
            const res = await apiHelper(`sales/promotional-campaigns/${row.id}/download/`, 'GET');
            if (res && res.ok) {
                const blob = await res.blob();
                const url  = window.URL.createObjectURL(blob);
                const a    = document.createElement('a');
                a.href     = url;
                a.download = `${row.name || 'Campaign'}.xlsx`;
                a.click();
                window.URL.revokeObjectURL(url);
            }
        } catch (err) {
            console.error('Download error:', err);
        } finally {
            setIsDownloading(false);
        }
    };

    if (!row) return null;

    const cfg = STATUS_CONFIG[row.status] || STATUS_CONFIG['Ended-Early'];

    // ── Copy Open ID ──
    const handleCopy = async (text) => {
        if (!text) return;
        try {
            if (window.isSecureContext && navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const ta = document.createElement('textarea');
                ta.value = text;
                ta.style.cssText = 'position:fixed;opacity:0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* silent */ }
    };

    // ── Items tab logic ──
    const handleItemSort = (field) => {
        if (itemSortField === field) {
            setItemSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setItemSortField(field);
            setItemSortOrder('asc');
        }
        setItemPage(1);
    };

    const filteredItems = items.filter(item => {
        if (!itemSearch.trim()) return true;
        const q = itemSearch.toLowerCase();
        return (
            (item.model     || '').toLowerCase().includes(q) ||
            (item.storage   || '').toLowerCase().includes(q) ||
            (item.color     || '').toLowerCase().includes(q) ||
            (item.condition || '').toLowerCase().includes(q)
        );
    }).sort((a, b) => {
        if (!itemSortField) return 0;
        const aVal = a[itemSortField] ?? '';
        const bVal = b[itemSortField] ?? '';
        if (aVal < bVal) return itemSortOrder === 'asc' ? -1 : 1;
        if (aVal > bVal) return itemSortOrder === 'asc' ?  1 : -1;
        return 0;
    });

    const itemTotalPages = Math.ceil(filteredItems.length / ITEMS_PAGE_SIZE);
    const itemPageData   = filteredItems.slice((itemPage - 1) * ITEMS_PAGE_SIZE, itemPage * ITEMS_PAGE_SIZE);
    const itemRangeStart = filteredItems.length === 0 ? 0 : (itemPage - 1) * ITEMS_PAGE_SIZE + 1;
    const itemRangeEnd   = Math.min(itemPage * ITEMS_PAGE_SIZE, filteredItems.length);

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            {/* Overlay */}
            <div className="drawer-overlay" onClick={onClose} />

            {/* Drawer panel */}
            <div className="entity-drawer">

                {/* ── Drawer Header ── */}
                <div className="drawer-header" style={{ backgroundColor: '#F8F9FA',padding:'15px 20px' }}>
                    <h3>Campaign Info</h3>
                    <button className="close-btn" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="main-drawer-inner">

                    {/* Title row: name + actions */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'nowrap', width: '100%',padding:'0px 0px 15px 0px' }}>
                        <h2 style={{ textTransform: 'none', margin: 0,
                            color:  '#212529',
                            fontFamily: 'Montserrat',
                            fontSize: '20px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: 'normal',
                         }} className="pb-0">
                            {row.name}
                        </h2>

                        {/* Edit + more-options */}
                        <div className="entities-tab p-0">
                            <div className="toolbar">
                                <div className="toolbar-right" style={{ position: 'relative', flexWrap: 'nowrap' }}>
                                    <div className="edit-organisation-info d-flex justify-content-end align-items-center gap-2"
                                        style={{ flexShrink: 0, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {(row.status === 'Upcoming' || row.status === 'Ongoing') && (
                                            <button className="dark-border gray-btn" type="button" style={{ width: '100px',height:'34px',padding:'8px' }} onClick={() => onEdit && onEdit(row)}>
                                                Edit
                                            </button>
                                        )}
                                    </div>
                                    {(row.status === 'Upcoming' || row.status === 'Ongoing') && (
                                        <>
                                            <button
                                                className="drawer-dots"
                                                style={{ boxShadow: 'none' }}
                                                onClick={(e) => { e.stopPropagation(); setMoreOpen(prev => !prev); }}
                                            >
                                                <MoreHorizontal size={18} />
                                            </button>

                                            {moreOpen && (
                                                <div
                                                    className="backdrop-cls"
                                                    style={{ position: 'absolute', top: 40, right: 0, background: 'white', boxShadow: '0px 1px 4px 0px #0000001A', zIndex: 1000, minWidth: 152, borderRadius: 8 }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <div className="btn-align-forall">
                                                        {row.status === 'Upcoming' && (
                                                            <div className="common-for-all">
                                                                <button className="deactivate-cls" onClick={() => { setMoreOpen(false); setShowCancelModal(true); }}>Cancel</button>
                                                            </div>
                                                        )}
                                                        {row.status === 'Ongoing' && (
                                                            <div className="common-for-all">
                                                                <button className="deactivate-cls" onClick={() => { setMoreOpen(false); setShowEndModal(true); }}>End</button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Tabs ── */}
                    <div className="drawer-tabs" style={{ padding: '20px 0px', gap: '20px', marginBottom: '0px', borderBottom: 'none' }}>
                        <button className={activeTab === 'general' ? 'active' : ''} onClick={() => setActiveTab('general')}>
                            General
                        </button>
                        <button className={activeTab === 'items' ? 'active' : ''} onClick={() => setActiveTab('items')}>
                            Items
                        </button>
                    </div>

                    {/* ── Tab Content ── */}
                    <div className="drawer-content">

                        {/* ══════════ GENERAL TAB ══════════ */}
                        {activeTab === 'general' && (
                            <div className="tab-panel general-panel">
                                <div className="card-section" style={{ borderRadius: 12, boxShadow: '0px 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 20 }}>

                                    {/* Card header */}
                                    <div className="card-header" style={{ backgroundColor: '#F8F9FA', padding: '15px 20px', fontFamily: 'Montserrat', fontSize: 14, fontWeight: 600, color: '#212529', display: 'flex', alignItems: 'center', height: 50, borderBottom: 'none', borderRadius: '12px 12px 0 0' }}>
                                        Basic Info
                                    </div>

                                    {/* Card body — 2-col grid */}
                                    <div className="card-body" style={{ backgroundColor: '#FFF', padding: '10px 20px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>

                                        {/* Campaign Name */}
                                        <div className="field-block" style={{ borderBottom: 'none', padding: 0 }}>
                                            <div className="field-label">Campaign Name</div>
                                            <div className="field-value" style={{fontSize:'12px'}}>{row.name || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>

                                        {/* Status */}
                                        <div className="field-block" style={{ borderBottom: 'none', padding: 0 }}>
                                            <div className="field-label">Status</div>
                                            <div className="field-value" style={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: 'Montserrat', background: cfg.bg, color: cfg.color, border: cfg.border, whiteSpace: 'nowrap', width: 'fit-content' }}>
                                                    {row.status}
                                                </span>
                                                {/* {row.statusNote && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dotColor, flexShrink: 0 }} />
                                                        <span style={{ fontSize: 11, fontFamily: 'Montserrat', color: cfg.dotColor }}>{row.statusNote}</span>
                                                    </div>
                                                )} */}
                                            </div>
                                        </div>

                                        {/* Item Qty */}
                                        <div className="field-block" style={{ borderBottom: 'none', padding: 0 }}>
                                            <div className="field-label">Item Qty</div>
                                            <div className="field-value">{row.itemQty ?? <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>

                                        {/* Promotional Period */}
                                        <div className="field-block" style={{ borderBottom: 'none', padding: 0 }}>
                                            <div className="field-label">Promotional Period</div>
                                            <div className="field-value" style={{ whiteSpace: 'nowrap' }}>
                                                {row.status === 'Cancelled' || !(row.promoStart && row.promoEnd)
                                                    ? '-'
                                                    : `${row.promoStart} - ${row.promoEnd}`}
                                            </div>
                                        </div>

                                        {/* Open ID — full width */}
                                        <div className="field-block" style={{ borderBottom: 'none', gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', padding: 0 }}>
                                            <div className="field-label" style={{ marginBottom: 6 }}>Open ID</div>
                                            <div className="field-value" style={{ wordBreak: 'break-all' }}>
                                                {row.open_id ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <span style={{ color: '#0D6EFD', textDecoration: 'underline', fontWeight: 500, cursor: 'pointer' }}
                                                            onClick={() => handleCopy(row.open_id)}>
                                                            {row.open_id}
                                                        </span>
                                                        <Copy size={13} style={{ color: '#0D6EFD', cursor: 'pointer', flexShrink: 0 }} onClick={() => handleCopy(row.open_id)} />
                                                        {copied && <span style={{ fontSize: 12, color: '#0D6EFD' }}>Copied!</span>}
                                                    </div>
                                                ) : <span style={{ color: '#ADB5BD' }}>NA</span>}
                                            </div>
                                        </div>

                                        {/* Created By */}
                                        <div className="field-block" style={{ borderBottom: 'none', padding: 0 }}>
                                            <div className="field-label">Created By</div>
                                            <div className="field-value">{row.createdBy || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>

                                        {/* Created Time */}
                                        <div className="field-block" style={{ borderBottom: 'none', padding: 0 }}>
                                            <div className="field-label">Created Time</div>
                                            <div className="field-value">{row.createdTime || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>

                                        {/* Last Modified By */}
                                        <div className="field-block" style={{ borderBottom: 'none', padding: 0 }}>
                                            <div className="field-label">Last Modified By</div>
                                            <div className="field-value">{row.lastModifiedBy || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>

                                        {/* Last Modified Time */}
                                        <div className="field-block" style={{ borderBottom: 'none', padding: 0 }}>
                                            <div className="field-label">Last Modified Time</div>
                                            <div className="field-value">{row.lastModified || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ══════════ ITEMS TAB ══════════ */}
                        {activeTab === 'items' && (
                            <div className="tab-panel">

                                {/* Toolbar: search + download */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20,width:'310px' }}>
                                    <div className="search-box" style={{ flex: 1, position: 'relative', border: '1px solid #6C757D', background: '#FFF', width: '260px', height: '40px', padding: '10px' }}>
                                        <Search size={18} color="#6C757D" />
                                        <input
                                            type="text"
                                            placeholder="Search..."
                                            value={itemSearchInput}
                                            onChange={e => setItemSearchInput(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { setItemSearch(itemSearchInput); setItemPage(1); } }}
                                            style={{ border: 'none', outline: 'none', flex: 1, fontSize: 14, background: 'transparent', fontFamily: 'Montserrat' }}
                                        />
                                        {itemSearch && (
                                            <div
                                                onClick={() => { setItemSearchInput(''); setItemSearch(''); setItemPage(1); }}
                                                style={{ padding: '0 4px', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                    <path d="M9 3L3 9M3 3L9 9" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleDownload}
                                        disabled={isDownloading}
                                        style={{ height: 40, width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #6C757D', borderRadius: 8, background: '#FFF', cursor: isDownloading ? 'not-allowed' : 'pointer', flexShrink: 0, opacity: isDownloading ? 0.6 : 1 }}
                                        title="Download"
                                    >
                                        {isDownloading
                                            ? <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #CED4DA', borderTopColor: '#6C757D', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                            : <Download size={16} color="#6C757D" />
                                        }
                                    </button>
                                </div>

                                {/* Items table */}
                                <div className="table-wrapper" style={{ border: 'none' }}>
                                    {itemsLoading ? (
                                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#6C757D', fontSize: 13, fontFamily: 'Montserrat' }}>Loading...</div>
                                    ) : (
                                    <table className="entities-table" style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Montserrat', fontSize: 13 }}>
                                        <thead>
                                            <tr>
                                                <SortableCol label="Listing Model" field="model"     sortField={itemSortField} sortOrder={itemSortOrder} onSort={handleItemSort} style={{ width: '40%' }} />
                                                <SortableCol label="Listing Item"  field="storage"   sortField={itemSortField} sortOrder={itemSortOrder} onSort={handleItemSort} style={{ width: '35%' }} />
                                                <SortableCol label="Condition"     field="condition" sortField={itemSortField} sortOrder={itemSortOrder} onSort={handleItemSort} style={{ width: '25%' }} />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itemPageData.length === 0 ? (
                                                <tr>
                                                    <td colSpan={3} style={{ textAlign: 'center', padding: '40px 0', color: '#6C757D', fontSize: 13 }}>
                                                        No items found.
                                                    </td>
                                                </tr>
                                            ) : (
                                                itemPageData.map((item, idx) => (
                                                    <tr key={item.id} style={{ borderBottom: '1px solid #DEE2E6', background: idx % 2 === 0 ? '#FFF' : '#FAFAFA' }}>
                                                        {/* Listing Model */}
                                                        <td style={{ padding: '10px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <img src={item.image || noImage} alt={item.model} style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} onError={e => { e.target.src = noImage; }} />
                                                                <span style={{ fontWeight: 400, color: '#212529', fontSize: 12 }}>{item.model || '—'}</span>
                                                            </div>
                                                        </td>
                                                        {/* Listing Item */}
                                                        <td style={{ padding: '10px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <img src={item.image || noImage} alt={item.model} style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} onError={e => { e.target.src = noImage; }} />
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                                    <span style={{ fontSize: 12, color: '#212529' }}>{item.storage || '—'}</span>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                        <ColorCircle color={item.color_code || '#ccc'} />
                                                                        <span style={{ fontSize: 10, color: '#6C757D' }}>{item.color || '—'}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        {/* Condition */}
                                                        <td style={{ padding: '10px' }}>
                                                            {item.condition === 'New' ? (
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 12px', borderRadius: 99, fontSize: 11, fontWeight: 600, fontFamily: 'Montserrat', background: '#FFEB00', color: '#212529', border: '1px solid #FFEB00' }}>New</span>
                                                            ) : (
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 12px', borderRadius: 99, fontSize: 11, fontWeight: 600, fontFamily: 'Montserrat', background: '#FFEB00', color: '#212529', border: '1px solid #FFEB00' }}>{item.condition || 'Used'}</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                    )}
                                </div>

                                {/* Items pagination */}
                                <div style={{ display: 'flex', justifyContent: 'end', alignItems: 'center', marginTop: 12, paddingTop: 8 }}>
                                    
                                    <Pagination
                                        currentPage={itemPage}
                                        totalPages={itemTotalPages}
                                        onPageChange={setItemPage}
                                    />
                                </div>

                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style jsx>{`
                .search-box {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 6px 10px;
                    border-radius: 8px;
                }
                .search-box input::placeholder {
                    color: #ADB5BD;
                }
            `}</style>

            {/* ── Cancel Confirmation Modal ── */}
            <DiscardConfirmationModal
                isOpen={showCancelModal}
                entityName="Cancel Campaign"
                onCancel={() => setShowCancelModal(false)}
                onConfirm={() => {
                    if (onCancelCampaign) onCancelCampaign(row.id);
                    setShowCancelModal(false);
                }}
            />

            {/* ── End Confirmation Modal ── */}
            <DiscardConfirmationModal
                isOpen={showEndModal}
                entityName="End Campaign"
                onCancel={() => setShowEndModal(false)}
                onConfirm={() => {
                    if (onEndCampaign) onEndCampaign(row.id);
                    setShowEndModal(false);
                }}
            />
        </>
    );
};

export default CampaignInfoDrawer;
